/**
 * en.ts — English dictionary. Mirrors ar.ts key-for-key; the `Dictionary` type
 * makes a missing key a compile error, so the two can never drift apart.
 */
import type { Dictionary } from './ar';

export const EN_DICTIONARY: Dictionary = {
    // ── Common ──────────────────────────────────────────────────────────
    'common.language': 'Language',
    'common.arabic': 'Arabic',
    'common.english': 'English',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'common.save': 'Save',
    'common.loading': 'Loading…',
    'common.close': 'Close',
    'common.retry': 'Try again',
    'common.menu': 'Menu',
    'common.settings': 'Settings',
    'common.play': 'Play',
    'common.pause': 'Pause',
    'common.none': '—',

    // ── Layer names ─────────────────────────────────────────────────────
    'layer.wind': 'Wind',
    'layer.wind-gusts': 'Wind gusts',
    'layer.radar-sim': 'Forecast radar',
    'layer.precipitation': 'Precipitation',
    'layer.temperature': 'Temperature',
    'layer.feels-like': 'Feels like',
    'layer.wet-bulb': 'Wet bulb',
    'layer.k-index': 'Storm index',
    'layer.o-index': 'Thunderstorms',
    'layer.pressure': 'Pressure',
    'layer.humidity': 'Relative humidity',
    'layer.dewpoint': 'Dew point',
    'layer.clouds': 'Clouds',
    'layer.radar': 'Rain Radar',
    'layer.satellite': 'Satellite',
    'layer.hurricanes': 'Hurricanes',
    'layer.wildfires': 'Wildfires',

    'layerGroup.satellite': 'Satellite',
    'layerGroup.radar': 'Rain Radar',
    'layerGroup.radar-sim': 'Forecast radar (model)',
    'layerGroup.precipitation': 'Precipitation',
    'layerGroup.wind': 'Wind',
    'layerGroup.temperature': 'Temperature',
    'layerGroup.k-index': 'Storm index',
    'layerGroup.humidity': 'Humidity',
    'layerGroup.pressure': 'Pressure',
    'layer.t850-anomaly': '850 hPa temp anomaly',
    'layerDesc.t850-anomaly': 'Air temperature at 850 hPa (about 1.5 km up) vs its 1981-2010 climate average - shows the air mass itself, free of surface noise.',
    'layerGroup.t850-anomaly': '850 hPa temp anomaly',
    'layer.sst-anomaly-rel': 'Ocean temp anomaly (relative)',
    'layerDesc.sst-anomaly-rel': 'Sea surface temperature anomaly with the tropical mean removed - isolates El Nino from overall ocean warming.',
    'layerVariant.sst-anomaly': 'Absolute',
    'layerVariant.sst-anomaly-rel': 'Relative',
    'legend.sstAnomalyRel': 'Sea surface temp anomaly ({unit}) - relative, tropical mean removed',
    'layer.sst-anomaly': 'Ocean temp anomaly',
    'layerDesc.sst-anomaly': 'Sea surface temperature vs its 1971-2000 climate average. Red is warmer than normal, blue cooler - the measure of El Nino and La Nina.',
    'layerGroup.sst-anomaly': 'Ocean temperature',
    'layerVariant.wind': 'Wind speed',
    'layerVariant.wind-gusts': 'Wind gusts',
    'layerVariant.temperature': 'Actual',
    'layerVariant.feels-like': 'Feels like',
    'layerVariant.wet-bulb': 'Wet bulb',
    'layerVariant.k-index': 'K-Index (potential)',
    'layerVariant.o-index': 'Thunderstorms',
    'layerVariant.humidity': 'Relative',
    'layerVariant.dewpoint': 'Dew point',

    'layerDesc.wind': 'Wind speed map with animated particles on top showing direction and motion.',
    'layerDesc.wind-gusts': 'Peak forecast gust speed — useful for telling hazardous areas apart from average wind.',
    'layerDesc.radar-sim': 'Radar reflectivity as simulated by the GFS model for every coming hour, worldwide — a forecast, not an observation, in the same colours as the rain radar.',
    'layerDesc.precipitation': 'Rain and snow come from the model selected above the map; particles are drawn as snow where the model temperature is low.',
    'layerDesc.temperature': 'Air temperature two metres above the surface.',
    'layerDesc.feels-like': 'Apparent temperature after the effect of humidity and wind.',
    'layerDesc.wet-bulb': 'Wet-bulb temperature — a heat-stress indicator; above ~31°C is dangerous and ~35°C is the human survivability limit.',
    'layerDesc.k-index': 'Thunderstorm likelihood derived from mid-level instability and moisture; higher values mean a more storm-favourable environment.',
    'layerDesc.o-index': 'Convective Available Potential Energy (CAPE) from the selected model — the same field shown by Windy\'s "Thunder" layer; higher values mean an environment more favourable for stronger thunderstorms.',
    'layerDesc.pressure': 'Surface pressure from the model, and the basis for the isobar lines.',
    'layerDesc.humidity': 'Relative humidity two metres above the surface.',
    'layerDesc.dewpoint': 'A separate layer for reading absolute moisture, not just the percentage.',
    'layerDesc.clouds': 'Forecast cloud cover from the model.',
    'layerDesc.radar': 'Near-real-time observed rainfall, not a model forecast.',
    'layerDesc.satellite': 'Free satellite imagery over WMTS; it may lag behind real time.',
    'layerDesc.hurricanes': 'Tropical system tracker; an official source is still needed for production.',
    'layerDesc.wildfires': 'FIRMS hotspots; a free API key is needed for production.',

    // ── Layer panel ─────────────────────────────────────────────────────
    'layers.title': 'Weather maps',
    'layers.open': 'Open layers',
    'layers.close': 'Close layers',
    'layers.sidebarTitle': 'Layers',
    'layers.sectionObservation': 'Satellite & Radar',
    'layers.sectionWeather': 'Weather',
    'layers.sectionOverlays': 'Map overlays',
    'layers.forecastGroup': 'Forecast layers',
    'layers.tileGroup': 'Radar and satellite',
    'layers.modelPending': 'Layers for this model are still being prepared.',
    'layers.modelPendingLong': 'Layers for this model are still being prepared on the server — pick another model for now.',

    // ── Floating toggles ────────────────────────────────────────────────
    'toggle.particles.show': 'Show wind particles',
    'toggle.particles.hide': 'Hide wind particles',
    'toggle.particles.label': 'Wind particles',
    'toggle.isobars.show': 'Show isobars',
    'toggle.isobars.hide': 'Hide isobars',
    'toggle.isobars.label': 'Isobars',
    // Forecast, not observation — see the Arabic note and LightningLayer.tsx.
    'toggle.lightning.show': 'Show lightning forecast with rain',
    'toggle.lightning.hide': 'Hide lightning forecast',
    'toggle.lightning.label': 'Lightning forecast',
    'toggle.hurricanes.show': 'Show hurricanes',
    'toggle.hurricanes.hide': 'Hide hurricanes',
    'cyclone.tropicalStorm': 'Tropical storm',
    'cyclone.category': 'Category {n}',
    'cyclone.wind': 'Max wind speed',
    'cyclone.pressure': 'Central pressure',
    'cyclone.direction': 'Direction',
    'cyclone.movement': 'Movement speed',
    'cyclone.track': 'Forecast track',
    'cyclone.now': 'Now',
    'cyclone.colDate': 'Date',
    'cyclone.colTime': 'Time',
    'cyclone.colCat': 'Cat',
    'cyclone.colWind': 'Wind',
    'cyclone.colPressure': 'Pressure',
    'cyclone.forecastMark': 'forecast',
    'cyclone.source': 'Source',
    'attrib.title': 'Data sources',
    'attrib.short': 'Sources',
    'attrib.basemap': 'Base map',
    'attrib.gfs': 'GFS model',
    'attrib.ecmwf': 'ECMWF model',
    'attrib.icon': 'ICON model',
    'attrib.satellite': 'Satellite imagery',
    'attrib.meteosat': 'Meteosat imagery',
    'attrib.radar': 'Observed radar',
    'attrib.lightning': 'Observed lightning',
    'attrib.cyclones': 'Cyclones',
    'attrib.wildfires': 'Wildfires',
    'attrib.point': 'Point forecast',
    'attrib.places': 'Place names',
    'cyclone.hoursAhead': '+{n}h',
    'cyclone.close': 'Close',
    'cyclone.activeSystems': 'Active systems',
    'cyclone.legend': 'Symbol guide',
    'cyclone.tabTracking': 'Tracking',
    'cyclone.displayOptions': 'Display options',
    'cyclone.showSystems': 'Systems',
    'cyclone.showDisturbances': 'Development areas',
    'cyclone.showPast': 'Past',
    'cyclone.showCurrent': 'Current',
    'cyclone.showFuture': 'Forecast',
    'cyclone.pointStyle': 'Track-point style',
    'cyclone.pointStyleCyclone': 'Cyclone icons',
    'cyclone.pointStyleDot': 'Simple dots',
    'overlay.crosshair': 'Crosshair',
    'overlay.labels': 'Regions',
    'overlay.alerts': 'NWS warnings (US)',
    'overlay.labelValues': 'Region values',
    'cyclone.disturbances': 'Tropical disturbances',
    'cyclone.developmentArea': 'Potential development area',
    'cyclone.prob2day': '2-day: {n}%',
    'cyclone.prob7day': '7-day: {n}%',
    'cyclone.distArea': '{basin} area {n}',
    'cyclone.distProb': 'Formation chance: {p2}% in 2 days · {p7}% in 7 days',
    'cyclone.basin.Atlantic': 'Atlantic',
    'cyclone.basin.Pacific': 'Pacific',
    'cyclone.tabLegend': 'Legend',
    'cyclone.legendStrength': 'Intensity scale',
    'cyclone.level.cat-5': 'Hurricane Category 5',
    'cyclone.level.cat-4': 'Hurricane Category 4',
    'cyclone.level.cat-3': 'Hurricane Category 3',
    'cyclone.level.cat-2': 'Hurricane Category 2',
    'cyclone.level.cat-1': 'Hurricane Category 1',
    'cyclone.level.storm': 'Tropical Storm',
    'cyclone.level.depression': 'Tropical Depression',
    'cyclone.level.post': 'Post-Tropical Cyclone',
    'cyclone.level.remnant': 'Remnants',
    'cyclone.level.disturbance': 'Disturbance',
    'cyclone.legendColors': 'Colours',
    'cyclone.legendYellow': 'Tropical storm and categories 1–2',
    'cyclone.legendRed': 'Category 3 and above (major hurricane)',
    'cyclone.legendSymbols': 'Track symbols',
    'cyclone.legendSolid': 'Solid line — track travelled',
    'cyclone.legendDashed': 'Dashed line — forecast track',
    'cyclone.legendCone': 'Translucent circle — cone of uncertainty',
    'cyclone.legendDots': 'Small icons — timed positions along the track',
    'cyclone.compass.N': 'N',
    'cyclone.compass.NE': 'NE',
    'cyclone.compass.E': 'E',
    'cyclone.compass.SE': 'SE',
    'cyclone.compass.S': 'S',
    'cyclone.compass.SW': 'SW',
    'cyclone.compass.W': 'W',
    'cyclone.compass.NW': 'NW',
    'toggle.wildfires.show': 'Show wildfires',
    'toggle.wildfires.hide': 'Hide wildfires',

    // ── Right toolbar ───────────────────────────────────────────────────
    'toolbar.search': 'Search',
    'toolbar.myLocation': 'My location',
    'toolbar.zoomIn': 'Zoom in',
    'toolbar.zoomOut': 'Zoom out',
    'toolbar.fullscreen': 'Fullscreen',
    'toolbar.share': 'Share',
    'toolbar.info': 'About',
    'toolbar.measure': 'Measure',
    'toolbar.more': 'More tools',
    'measure.stop': 'Stop measuring',

    // ── Measure tools (distance / area) ─────────────────────────────────
    'measure.distanceTitle': 'Measure distance',
    'measure.areaTitle': 'Measure area',
    'measure.distance': 'Distance',
    'measure.area': 'Area',
    'measure.hint': 'Click the map to add points. Double-click to finish.',
    'measure.undo': 'Undo',
    'measure.clear': 'Clear',
    'search.placeholder': 'Search for a city…',
    'search.close': 'Close search',
    'search.loading': 'Searching…',
    'search.hint': 'Type a city name to search',
    'search.empty': 'No results',
    'geo.unsupported': 'Your browser does not support geolocation',
    'geo.failed': 'Could not determine your location',
    'location.none': 'No location selected',

    'about.title': 'About Sky Radar',
    'about.cityTitle': 'City weather',
    'about.cityLink': 'Browse city weather worldwide',
    'about.whatTitle': 'What is this?',
    'about.whatBody': 'An interactive platform for real-time weather around the world.',
    'about.sourcesTitle': 'Data sources',
    'about.sourceGfs': 'NOAA, United States (open government data)',
    'about.sourceEcmwf': 'European Centre (Open Data)',
    'about.sourceRainviewer': 'Observed rain radar',
    'about.sourceNasa': 'Satellite imagery and wildfires',
    'about.mapTitle': 'The map',
    'about.mapBody': '© OpenStreetMap, rendered with MapLibre GL JS',
    'about.updatedTitle': 'Last updated',
    'about.followTitle': 'Follow us',

    'social.facebook': 'Facebook',
    'social.x': 'X',
    'social.instagram': 'Instagram',
    'social.tiktok': 'TikTok',
    'social.threads': 'Threads',

    'report.title': 'Report a problem',
    'report.hint': 'The best way to describe the problem is to send us screenshots or a video, with a brief description of it and how to reproduce it.',
    'report.placeholder': 'Describe the problem you ran into…',
    'report.attach': 'Attach a screenshot or video',
    'report.attachmentTooLarge': 'File is too large — 10MB maximum.',
    'report.submit': 'Send us a bug report',
    'report.submitFailed': 'Could not send the report — check your connection and try again.',
    'report.thanks': 'Your report reached us — thanks for helping improve Sky Radar.',

    'share.title': 'Share location',
    'share.text': 'Follow the weather on Sky Radar',
    'share.whatsapp': 'WhatsApp',
    'share.telegram': 'Telegram',
    'share.facebook': 'Facebook',
    'share.email': 'Email',
    'share.copyLink': 'Copy link',
    'share.copy': 'Copy',
    'share.copied': 'Copied',

    // ── Settings ────────────────────────────────────────────────────────
    'settings.title': 'Settings',
    'settings.close': 'Close settings',
    'settings.units.title': 'Units',
    'settings.units.temperature': 'Temperature',
    'settings.units.celsius': '°Celsius',
    'settings.units.fahrenheit': '°Fahrenheit',
    'settings.units.wind': 'Wind',
    'settings.units.kmh': 'km/h',
    'settings.units.mph': 'mph',
    'settings.units.ms': 'm/s',
    'settings.units.knots': 'knots',
    'settings.units.pressure': 'Pressure',
    'settings.units.hpa': 'hPa',
    'settings.units.inhg': 'inHg',
    'settings.units.precipitation': 'Precipitation',
    'settings.units.mm': 'mm',
    'settings.units.inch': 'inch',
    'settings.about.title': 'About',
    'settings.about.tagline': 'A live weather platform for the whole world',
    'settings.about.version': 'Version {v}',
    'settings.about.data': 'Data',
    'settings.language.title': 'Interface language',

    // ── Models ──────────────────────────────────────────────────────────
    'model.title': 'Weather model',
    'model.tooltip': 'Model: {name} ({resolution})',
    'model.provider.GFS': 'NOAA, United States',
    'model.provider.ECMWF': 'ECMWF, Europe',
    'model.provider.ICON': 'DWD, Germany',
    'model.resolution': '{km} km',
    'settings.model.title': 'Forecast model',
    'settings.model.cycle': 'Model run',
    'settings.model.age': 'Data age',
    'settings.model.generated': 'Last generated',
    'settings.model.next': 'Next update',
    'settings.model.ago': '{d} ago',
    'settings.model.in': 'in {d}',
    'settings.model.due': 'due now',
    'settings.model.unavailable': 'No data has been generated for this model yet.',
    'settings.model.note': 'The update time is an estimate based on when the previous run was published.',
    'settings.model.staleNote': 'The data is stale — generation on the server appears to have stopped.',

    'duration.d': 'd',
    'duration.h': 'h',
    'duration.m': 'm',
    'duration.s': 's',

    // ── Loading toast ───────────────────────────────────────────────────
    'loading.model': 'Loading the {name} model',
    'loading.layer': 'Loading {name}',
    'loading.layerShort.wind': 'wind',
    'loading.layerShort.wind-gusts': 'wind gusts',
    'loading.layerShort.radar-sim': 'forecast radar',
    'loading.layerShort.precipitation': 'precipitation',
    'loading.layerShort.temperature': 'temperature',
    'loading.layerShort.feels-like': 'feels like',
    'loading.layerShort.wet-bulb': 'wet bulb',
    'loading.layerShort.k-index': 'K-Index',
    'loading.layerShort.o-index': 'thunderstorms',
    'loading.layerShort.pressure': 'pressure',
    'loading.layerShort.humidity': 'humidity',
    'loading.layerShort.dewpoint': 'dew point',
    'loading.layerShort.clouds': 'clouds',
    // These seven were missing, so the loading pill showed the raw key to visitors.
    // The family is keyed by layer id: every id in `layer.*` needs a twin here.
    'loading.layerShort.radar': 'radar',
    'loading.layerShort.satellite': 'satellite',
    'loading.layerShort.hurricanes': 'hurricanes',
    'loading.layerShort.wildfires': 'wildfires',
    'loading.layerShort.t850-anomaly': '850 hPa anomaly',
    'loading.layerShort.sst-anomaly': 'ocean temp anomaly',
    'loading.layerShort.sst-anomaly-rel': 'ocean anomaly (relative)',

    // ── Legend ──────────────────────────────────────────────────────────
    'legend.wind': 'Wind speed ({unit})',
    'legend.wind-gusts': 'Wind gusts ({unit})',
    'legend.radar-sim': 'Forecast radar — GFS model ({unit})',
    'legend.temperature': 'Temperature ({unit})',
    'legend.feels-like': 'Feels like ({unit})',
    'legend.wet-bulb': 'Wet bulb ({unit})',
    'legend.dewpoint': 'Dew point ({unit})',
    'legend.k-index': 'K-Index — thunderstorm potential',
    'legend.o-index': 'Thunderstorms — convective instability (CAPE)',
    'legend.precipitation': 'Precipitation (3-hour total, {unit})',
    'legend.pressure': 'Air pressure ({unit})',
    'legend.humidity': 'Relative humidity (%)',
    'legend.clouds': 'Cloud cover (%)',
    'radar.intensity.trace': 'Trace',
    'radar.intensity.light': 'Light rain',
    'radar.intensity.moderate': 'Moderate rain',
    'radar.intensity.heavy': 'Heavy rain',
    'radar.intensity.violent': 'Violent rain',
    'radar.intensity.hail': 'Storm — hail likely',
    'overlay.aerial': 'Aerial imagery',
    'overlay.radarCoverage': 'Radar coverage',
    'overlay.timestamp': 'Time & date stamp',
    'stamp.updated': 'Updated',
    'overlay.lightningSound': 'Thunder sound',
    'legend.radar': 'Radar intensity ({unit})',
    'legend.t850Anomaly': '850 hPa temp anomaly ({unit}) vs 1981-2010 average',
    'legend.sstAnomaly': 'Sea surface temp anomaly ({unit}) vs 1971-2000 average',
    'unit.celsius': '°C',
    'unit.fahrenheit': '°F',
    'unit.kmh': 'km/h',
    'unit.mm': 'mm',
    'unit.inch': 'in',
    'unit.hpa': 'hPa',
    'unit.inhg': 'inHg',
    'unit.km': 'km',

    // ── Tooltip / precipitation ─────────────────────────────────────────
    'precip.none': 'No precipitation',
    'precip.drizzle': 'Light drizzle',
    'precip.light': 'Light rain',
    'precip.moderate': 'Moderate rain',
    'precip.heavy': 'Heavy rain',
    'precip.extreme': 'Extreme rain',
    'precip.accum3h': 'Precipitation (3h)',
    'snow.flurries': 'Light flurries',
    'snow.light': 'Light snow',
    'snow.moderate': 'Moderate snow',
    'snow.heavy': 'Heavy snow',
    'snow.extreme': 'Extreme snow',
    'snow.accum3h': 'Snowfall (3h)',

    'compass.N': 'N', 'compass.NNE': 'NNE', 'compass.NE': 'NE',
    'compass.ENE': 'ENE', 'compass.E': 'E', 'compass.ESE': 'ESE',
    'compass.SE': 'SE', 'compass.SSE': 'SSE', 'compass.S': 'S',
    'compass.SSW': 'SSW', 'compass.SW': 'SW', 'compass.WSW': 'WSW',
    'compass.W': 'W', 'compass.WNW': 'WNW', 'compass.NW': 'NW',
    'compass.NNW': 'NNW',

    // ── Timeline ────────────────────────────────────────────────────────
    'time.today': 'Today',
    'time.tomorrow': 'Tomorrow',
    'time.am': 'AM',
    'time.pm': 'PM',
    'time.jumpLatest': 'Jump to the latest frame',
    'time.frameReady': 'Ready — instant',
    'time.nowcast': 'Forecast',
    'time.frameLoading': 'Not preloaded — may wait to load',
    'time.play': 'Play animation',
    'time.pause': 'Pause animation',
    'month.1': 'January', 'month.2': 'February', 'month.3': 'March', 'month.4': 'April',
    'month.5': 'May', 'month.6': 'June', 'month.7': 'July', 'month.8': 'August',
    'month.9': 'September', 'month.10': 'October', 'month.11': 'November', 'month.12': 'December',
    // مختصرات الأشهر — لعمود التاريخ الضيّق في جدول الإعصار.
    'monthShort.1': 'Jan', 'monthShort.2': 'Feb', 'monthShort.3': 'Mar', 'monthShort.4': 'Apr',
    'monthShort.5': 'May', 'monthShort.6': 'Jun', 'monthShort.7': 'Jul', 'monthShort.8': 'Aug',
    'monthShort.9': 'Sep', 'monthShort.10': 'Oct', 'monthShort.11': 'Nov', 'monthShort.12': 'Dec',
    'day.0': 'Sunday', 'day.1': 'Monday', 'day.2': 'Tuesday', 'day.3': 'Wednesday',
    'day.4': 'Thursday', 'day.5': 'Friday', 'day.6': 'Saturday',

    // ── Weather info panel ──────────────────────────────────────────────
    'weather.selectedLocation': 'Selected location',
    'weather.hourly': 'Hourly forecast',
    'weather.fetching': 'Fetching weather data…',
    'weather.fetchFailed': 'Could not fetch weather data for this location.',
    'weather.updating': 'Updating…',
    'weather.feelsLike': 'Feels like {v}',
    'weather.cached': 'Showing a cached copy of the last available data.',
    'weather.colTemp': 'Temp',
    'weather.colPrecip': 'Rain',
    'weather.colState': 'Condition',
    'weather.colHour': 'Time',
    'weather.openDetails': 'Show weather details for this location',
    'coord.N': 'N', 'coord.S': 'S', 'coord.E': 'E', 'coord.W': 'W',

    'wx.Clear sky': 'Clear sky',
    'wx.Mainly clear': 'Mainly clear',
    'wx.Partly cloudy': 'Partly cloudy',
    'wx.Overcast': 'Overcast',
    'wx.Fog': 'Fog',
    'wx.Rime fog': 'Rime fog',
    'wx.Light drizzle': 'Light drizzle',
    'wx.Moderate drizzle': 'Moderate drizzle',
    'wx.Dense drizzle': 'Dense drizzle',
    'wx.Light rain': 'Light rain',
    'wx.Moderate rain': 'Moderate rain',
    'wx.Heavy rain': 'Heavy rain',
    'wx.Light showers': 'Light showers',
    'wx.Moderate showers': 'Moderate showers',
    'wx.Heavy showers': 'Heavy showers',
    'wx.Light snow': 'Light snow',
    'wx.Moderate snow': 'Moderate snow',
    'wx.Heavy snow': 'Heavy snow',
    'wx.Snow grains': 'Snow grains',
    'wx.Light snow showers': 'Light snow showers',
    'wx.Heavy snow showers': 'Heavy snow showers',
    'wx.Freezing drizzle': 'Freezing drizzle',
    'wx.Freezing rain': 'Freezing rain',
    'wx.Thunderstorm': 'Thunderstorm',
    'wx.Thunderstorm + hail': 'Thunderstorm with hail',
    'wx.Unknown': 'Unknown',

    // ── Map cannot run (WebGL) ──────────────────────────────────────────
    'webgl.title': 'The map cannot start',
    'webgl.body': 'Rendering the map needs WebGL, and your browser is not providing it right now — usually because hardware acceleration is off or the graphics driver is blocked.',
    'webgl.fixEnable': 'Turn on “Use graphics acceleration when available” in your browser’s system settings, then restart it.',
    'webgl.fixUpdate': 'Update your graphics driver, and restart the machine afterwards.',
    'webgl.fixBrowser': 'Try another up-to-date browser (Chrome, Edge or Firefox).',
    'webgl.reload': 'Try again',

    // ── Admin dashboard ─────────────────────────────────────────────────
    'admin.nav.overview': 'Overview',
    'admin.nav.layers': 'Layers and colours',
    'admin.nav.models': 'Weather models',
    'admin.nav.reports': 'Problem reports',
    'admin.nav.particles': 'Wind particles',
    'admin.nav.viewMap': 'View map',
    'admin.badge': 'Admin',
    'admin.header.title': 'Sky Radar admin',
    'admin.header.menu': 'Menu',
    'admin.logout': 'Sign out',
    'admin.login.subtitle': 'Admin sign-in',
    'admin.layers.enabledCount': '{on} of {total} enabled',
    'admin.layers.colorHelp': 'Used for the icon and quick identification inside the dashboard.',
    'admin.layers.gradientHelp': 'Explains what the colours on the map mean, e.g. light to heavy rain.',
    'admin.layers.opacityOrderHelp': 'Controls how visible the layer is and whether it sits above or below other layers.',
    'admin.layers.edit': 'Edit',
    'admin.layers.toastEnabled': 'Layer “{name}” enabled',
    'admin.layers.toastDisabled': 'Layer “{name}” hidden',
    'admin.models.edit': 'Edit {name}',
    'admin.models.providerPlaceholder': 'NOAA, United States',
    'admin.models.resolutionPlaceholder': '22 km',
    'admin.models.toastEnabled': 'Model {name} enabled',
    'admin.models.toastDisabled': 'Model {name} hidden',
    'admin.models.deleted': 'Model {name} deleted.',
    'admin.models.deleteConfirm': 'Model “{name}” will be removed from the settings (raster files are kept). Continue?',
    'admin.gradient.count': '{n} colour stops',
    'admin.gradient.addStop': 'Add stop',
    'admin.error.unexpected': 'Something went wrong.',
    'admin.login.email': 'Email address',
    'admin.login.password': 'Password',
    'admin.login.submit': 'Sign in',
    'admin.login.submitting': 'Signing in…',

    'admin.dash.visitsWindow': 'Visits ({window})',
    'admin.dash.today': 'Today: {n}',
    'admin.dash.goLayers': 'Go to layers →',
    'admin.dash.goModels': 'Go to models →',
    'admin.dash.range7': '7 days',
    'admin.dash.range30': '30 days',
    'admin.dash.range90': '90 days',
    'admin.dash.noData': 'No data yet',
    'admin.dash.homePage': 'Home page',
    'admin.dash.trackingMissing': 'Visitor tracking is not deployed yet — run the migration on the server.',
    'admin.dash.visitorsFailed': 'Could not fetch visitor data',
    'admin.dash.newUsers': 'New users',
    'admin.dash.enabledLayers': 'Enabled layers',
    'admin.dash.enabledModels': 'Enabled models',
    'admin.dash.lastEdit': 'Last edit',
    'admin.dash.visits': 'Visits',
    'admin.dash.visitorRate': 'Visitor rate',
    'admin.dash.newUsersChart': 'New users',
    'admin.dash.topCountries': 'Top countries',
    'admin.dash.topReferrers': 'Top referrers',
    'admin.dash.topPages': 'Top pages',
    'admin.dash.topDevices': 'Top devices',
    'admin.dash.topOs': 'Operating systems',
    'admin.dash.topBrowsers': 'Browsers',
    'admin.dash.manageLayers': 'Manage layers',
    'admin.dash.manageModels': 'Manage models',

    'admin.layers.title': 'Layers and colours',
    'admin.layers.colColor': 'Layer colour',
    'admin.layers.colGradient': 'Map gradient',
    'admin.layers.colOpacityOrder': 'Opacity and order',
    'admin.layers.colorTitle': 'Layer accent colour',
    'admin.layers.opacityTitle': 'Layer opacity over the map',
    'admin.layers.moveUp': 'Up',
    'admin.layers.moveDown': 'Down',
    'admin.layers.group.satellite': 'Satellite',
    'admin.layers.group.radar': 'Radar',
    'admin.layers.group.precipitation': 'Precipitation',
    'admin.layers.group.wind': 'Wind',
    'admin.layers.group.temperature': 'Temperature',
    'admin.layers.group.storms': 'Storms',
    'admin.layers.group.humidity': 'Humidity',
    'admin.layers.group.pressure': 'Pressure',
    'admin.layers.group.extra': 'Extra',
    'admin.layers.group.sst-anomaly': 'Ocean temperature',
    'admin.layers.group.t850-anomaly': '850 hPa anomaly',
    'admin.layers.group.radar-sim': 'Forecast radar',
    'admin.layers.group.clouds': 'Clouds',
    'admin.layers.group.overlay': 'Overlays',

    'admin.layerEdit.back': 'Back',
    'admin.layerEdit.saved': 'Changes saved — reload the map page to see them.',
    'admin.layerEdit.restored': 'Default settings restored.',
    'admin.layerEdit.restoreDefaults': 'Restore defaults',
    'admin.layerEdit.restore': 'Restore',
    'admin.layerEdit.templateApplied': 'Applied the “{label}” template — reload the map page to see it.',
    'admin.layerEdit.templateActive': 'Active',
    'admin.layerEdit.confirmResetLayer': 'All customizations for the “{name}” layer (names, colors, and gradient) will be replaced with the defaults. Continue?',
    'admin.layerEdit.save': 'Save changes',
    'admin.layerEdit.saving': 'Saving…',
    'admin.layerEdit.basics': 'Basic settings',
    'admin.layerEdit.nameAr': 'Arabic name',
    'admin.layerEdit.nameEn': 'English name',
    'admin.layerEdit.accentColor': 'Icon / accent colour',
    'admin.layerEdit.icon': 'Icon',
    'admin.layerEdit.enabled': 'Layer enabled',
    'admin.layerEdit.disabled': 'Layer hidden from the map',
    'admin.layerEdit.enabling': 'Enabling…',
    'admin.layerEdit.palette': 'Colour preset',
    'admin.layerEdit.gradient': 'Map colour gradient',
    'admin.layerEdit.legend': 'Legend bar',
    'admin.layerEdit.legendTitle': 'Title',
    'admin.layerEdit.legendLabels': 'Labels (comma separated)',
    'admin.gradient.preview': 'Gradient preview on the map',
    'admin.gradient.stop': 'Colour stop',
    'admin.gradient.removeStop': 'Remove stop',
    'admin.gradient.value': 'Value',
    'admin.gradient.color': 'Colour',
    'admin.gradient.opacity': 'Opacity',

    'admin.models.title': 'Weather models',
    'admin.models.add': 'Add model',
    'admin.models.saved': 'Model saved.',
    'admin.models.key': 'Key',
    'admin.models.name': 'Display name',
    'admin.models.provider': 'Provider',
    'admin.models.resolution': 'Resolution',
    'admin.models.color': 'Colour',
    'admin.models.enabled': 'Enabled',
    'admin.models.delete': 'Delete',
    'admin.models.deleteTitle': 'Delete model',

    'admin.particles.title': 'Wind particle defaults',
    'admin.particles.section': 'Particle motion',
    'admin.particles.color': 'Particle colour',
    'admin.particles.saved': 'Defaults saved',
    'admin.particles.restoreConfirm': 'All default particle motion values will be replaced with the original ones. Continue?',
    'admin.particles.count': 'Number of streaks (density)',
    'admin.particles.count.min': 'Few',
    'admin.particles.count.max': 'Very dense',
    'admin.particles.speed': 'Streak acceleration (speed)',
    'admin.particles.speed.min': 'Low',
    'admin.particles.speed.max': 'High',
    'admin.particles.trail': 'Streak length (tail)',
    'admin.particles.trail.min': 'Short',
    'admin.particles.trail.max': 'Long',
    'admin.particles.opacity': 'Particle brightness',
    'admin.particles.opacity.min': 'Very transparent',
    'admin.particles.opacity.max': 'Opaque',
    'admin.particles.width': 'Average streak width',
    'admin.particles.width.min': 'Thin',
    'admin.particles.width.max': 'Wide',
    'admin.particles.widthJitter': 'Streak width scatter',
    'admin.particles.widthJitter.min': 'Uniform',
    'admin.particles.widthJitter.max': 'Varied',
    'admin.particles.life': 'Streak lifetime',
    'admin.particles.life.min': 'Brief',
    'admin.particles.life.max': 'Persistent',

    'admin.reports.title': 'Problem reports',
    'admin.reports.empty': 'No reports.',
    'admin.reports.total': '{n} reports',
    'admin.reports.openBadge': '{n} open reports',
    'admin.reports.status.open': 'Open',
    'admin.reports.status.resolved': 'Resolved',
    'admin.reports.pageUrl': 'Page',
    'admin.reports.attachment': 'Attachment',
    'admin.reports.deleteTitle': 'Delete report',
    'admin.reports.deleteConfirm': 'The report and its attachment will be deleted permanently. Continue?',
};
